import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAppStore = defineStore('app', () => {
  // 状态
  const isLoading = ref(false)
  const isMobile = ref(false)
  const sidebarCollapsed = ref(false)
  const theme = ref<'light' | 'dark'>('light')
  const notifications = ref<Array<{
    id: string
    type: 'success' | 'warning' | 'error' | 'info'
    title: string
    message?: string
    duration?: number
  }>>([])

  // 计算属性
  const hasNotifications = computed(() => notifications.value.length > 0)

  // 方法
  const initialize = () => {
    // 检测设备类型
    checkMobile()
    
    // 监听窗口大小变化
    window.addEventListener('resize', checkMobile)
    
    // 从本地存储恢复设置
    restoreSettings()
  }

  const checkMobile = () => {
    isMobile.value = window.innerWidth < 768
    if (isMobile.value) {
      sidebarCollapsed.value = true
    }
  }

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
    saveSettings()
  }

  const toggleTheme = () => {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
    applyTheme()
    saveSettings()
  }

  const applyTheme = () => {
    if (theme.value === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const addNotification = (notification: Omit<typeof notifications.value[0], 'id'>) => {
    const id = Date.now().toString()
    const newNotification = {
      id,
      duration: 5000,
      ...notification
    }
    
    notifications.value.push(newNotification)
    
    // 自动移除通知
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }
    
    return id
  }

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clearNotifications = () => {
    notifications.value = []
  }

  const saveSettings = () => {
    const settings = {
      sidebarCollapsed: sidebarCollapsed.value,
      theme: theme.value
    }
    localStorage.setItem('app-settings', JSON.stringify(settings))
  }

  const restoreSettings = () => {
    try {
      const saved = localStorage.getItem('app-settings')
      if (saved) {
        const settings = JSON.parse(saved)
        sidebarCollapsed.value = settings.sidebarCollapsed ?? false
        theme.value = settings.theme ?? 'light'
        applyTheme()
      }
    } catch (error) {
      console.warn('Failed to restore app settings:', error)
    }
  }

  return {
    // 状态
    isLoading,
    isMobile,
    sidebarCollapsed,
    theme,
    notifications,
    
    // 计算属性
    hasNotifications,
    
    // 方法
    initialize,
    toggleSidebar,
    toggleTheme,
    setLoading,
    addNotification,
    removeNotification,
    clearNotifications
  }
})